import React, {
  useState,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";

import { Grid } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Error } from "../../../../../../../models/error";
import { PriceGroup } from "../../../../../../../models/price.group";
import { Service as ServiceBooking } from "../../../../../../../models/service";
import PriceGroupService from "../../../../../../../services/price.group.service";
import ServiceBookingService from "../../../../../../../services/service.booking.service";
import { useAppState } from "../../../../../../../context/StateContext";
import { Status } from "../../../../../../../util/enums";
import AutoSuggest from "../../../../../../../components/Form/AutoSuggest";
import Input from "../../../../../../../components/Form/Input";
import Modal from "../../../../../../../components/Generic/Modal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import useStyles from "./styles";

export interface PriceGroupMaintenanceHandles {
  openModal: () => void;
  closeModal: () => void;
}

interface PriceGroupMaintenanceProps {}

const PriceGroupMaintenance: ForwardRefRenderFunction<
  PriceGroupMaintenanceHandles,
  PriceGroupMaintenanceProps
> = (props, ref) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { currentUser } = useAppState();

  const [modalOpened, setModalOpened] = useState(false);
  const [name, setName] = useState("");
  const [services, setServices] = useState<ServiceBooking[]>([]);
  const [allServices, setAllServices] = useState<ServiceBooking[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const clearFields = useCallback(() => {
    setName("");
    setErrors([]);
    setServices([]);
  }, []);

  const openModal = useCallback(() => {
    setModalOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    clearFields();
    setHasFormBeenSubmitted(false);
    setModalOpened(false);
  }, []);

  useImperativeHandle(ref, () => {
    return {
      openModal,
      closeModal,
    };
  });

  const { isFetching: loadingServices } = useQuery(
    ["service.list"],
    () =>
      ServiceBookingService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["sellDescription", "asc"]]),
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.companyId && !!modalOpened,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setAllServices(response.data);
      },
    }
  );

  const { mutate: createGroup, isLoading: creatingGroup } = useMutation(
    ["service.create"],
    (group: PriceGroup) => PriceGroupService.create(group),
    {
      onSuccess: () => {
        NotificationToast.success(t("operationPerformedSuccessfully"));
        closeModal();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    }
  );

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    name: yup.string().required(t("thisFieldIsRequired")),
  });

  const validateFields = useCallback((group: PriceGroup) => {
    const isSuccess = () => {
      schema.validateSync({ name: group.name }, { abortEarly: false });
      setErrors([]);

      return true;
    };
    const isError = (error: unknown) => {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    };

    try {
      return isSuccess();
    } catch (error) {
      return isError(error);
    }
  }, []);

  const onSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const group = {
      name,
      services,
      companyId: currentUser?.companyId,
      status: Status.ACTIVE,
    } as PriceGroup;

    if (validateFields(group)) {
      createGroup(group);
    }
  };

  const loading = !!creatingGroup || !!loadingServices;

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const group = {
        name,
      } as PriceGroup;

      validateFields(group);
    }
  }, [name, hasFormBeenSubmitted]);

  return (
    <Modal
      open={modalOpened}
      handleClose={closeModal}
      title={t("createNewGroup")}
      dataCyTitle="modal-group-maintenance"
      className={classes.modalWidth}
      data-cy="serviceModal"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        dataCySave: "submitButton",
        onCancel: () => closeModal(),
        loading,
        saveDisabled: loading,
        cancelDisabled: loading,
        textSave: t("create"),
        onSave: () => onSubmit(),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container>
          <Grid item md={6} lg={6}>
            <Input
              disabled={loading}
              name="name"
              label={t("name")}
              placeholder={t("name")}
              error={!!getErrorByField("name")}
              errorMessage={getErrorByField("name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
        </Grid>

        <Grid container className={classes.separator}></Grid>

        <Grid container>
          <Grid item xs={12} md={12} lg={12}>
            <AutoSuggest
              disabled={loading}
              data-cy="suggest-services"
              tagDataCy="item-service"
              label={t("services")}
              placeholder={t("searchService")}
              options={allServices}
              value={services}
              getOptionLabel={(service: ServiceBooking) =>
                service ? service.sellDescription || "" : ""
              }
              getOptionSelected={(option: ServiceBooking, value: ServiceBooking) =>
                option.id === value.id
              }
              multiple
              onChange={(services: ServiceBooking[]) => setServices(services)}
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default forwardRef(PriceGroupMaintenance);
